import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import BetTableDum10 from "../../casinoComponents/betTableDum10";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import MyBet from "../../casinoComponents/myBet";
import ViewMoreModal from "../../../components/ViewMoreModal";
import Dum10Video from "./Dum10Video";
import Dum10RefResults from "./Dum10RefResults";
import { getCasinoGameDetails, getMatchExposure, getMyBets } from "../../../apiservices/CasionApi";
import { extractCasinoGame } from "../../../utils/casinoGame";
import "../../../styles/games/dum10.css";

// Reference title (casino/lastresults2 res1.cname)
const DUM10_TITLE = "Dus ka Dum";

// Reference: _.filter(sub, { subtype }) ordered by sr (stable)
const bySubtype = (sub, subtype) =>
  (Array.isArray(sub) ? sub : [])
    .filter((s) => s?.subtype === subtype)
    .sort((a, b) => (Number(a?.sr) || 0) - (Number(b?.sr) || 0));

export default function Dum10() {
  const [gameData, setGameData] = useState(null);
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);

  const { pathname } = useLocation();
  const gameName = extractCasinoGame(pathname) || "dum10";
  const iframeSrc = `https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`;

  useEffect(() => {
    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails("dum10");
        setGameData(response);
      } catch (err) {
        console.error("Error fetching game data:", err);
      }
    };

    fetchGameData();
    const interval = setInterval(fetchGameData, 1500);
    return () => clearInterval(interval);
  }, [pathname]);

  const d = gameData?.data?.data || gameData?.data || {};
  const matchId = d?.mid?.toString() || gameData?.mid?.toString();

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;

      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            const teamName = item.team_name || item.selection || item.player_name || "";
            const exposureAmount = parseFloat(item.exposure_amount) || 0;
            if (teamName) {
              exposureMap[teamName] = exposureAmount;
              exposureMap[teamName.toLowerCase().trim()] = exposureAmount;
              exposureMap[teamName.trim()] = exposureAmount;
              exposureMap[teamName.toLowerCase()] = exposureAmount;
            }
          });
          setExposures(exposureMap);
        }
      }
    } catch (error) {
      console.error("Error fetching exposure:", error);
    }
  }, [matchId]);

  const fetchMyBetsData = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;

      if (userId && matchId) {
        const response = await getMyBets(userId, matchId);
        if (response.success && response.bets) {
          const formattedBets = response.bets.map((bet) => ({
            // My Bets "Gametype" column (casino_bets.game_name, e.g. mogambo)
            description: bet.description ?? bet.game_name ?? bet.gameName,
            userName: bet.userName ?? bet.username,
            placeDate: bet.placeDate ?? bet.created_at,
            matchedBet: bet.player_name || bet.selection || "",
            odds: bet.odds || "0",
            stake: bet.stake || "0",
            type: bet.type || null,
          }));
          setMyBets(formattedBets);
        }
      }
    } catch (error) {
      console.error("Error fetching my bets:", error);
    }
  }, [matchId]);

  useEffect(() => {
    if (matchId) {
      const refresh = () => {
        fetchExposure();
        fetchMyBetsData();
      };
      const first = setTimeout(refresh, 0);
      const exposureInterval = setInterval(refresh, 2000);
      return () => {
        clearTimeout(first);
        clearInterval(exposureInterval);
      };
    }
  }, [matchId, fetchExposure, fetchMyBetsData]);

  // Admin panel places no bets (the old PlaceBet slot was the NoBet stub): an odds click refreshes book + bets.
  const handleBetClick = (value) => {
    if (!value) return;
    fetchExposure();
    fetchMyBetsData();
  };

  const main = bySubtype(d?.sub, "dum10");
  const fancy = bySubtype(d?.sub, "dum10fancy");
  const roundId = d?.mid || "";

  // Reference .casino-table.duskadum: no .game-header — title, round id and current total sit in the video
  return (
    <CasinoTableLayout
      tableClass="duskadum"
      rightSide={<MyBet bets={myBets} onViewMore={() => setShowViewMoreModal(true)} />}
    >
      <>
        <Dum10Video title={DUM10_TITLE} data={d} iframeSrc={iframeSrc} resetKey={gameData} />{" "}
        {roundId ? (
          <div className="casino-detail">
            <BetTableDum10 main={main} fancy={fancy} onBetClick={handleBetClick} exposures={exposures} />{" "}
            {d?.remark ? (
              <div className="casino-remark mt-3">
                <div className="remark-icon">
                  <img src={`${import.meta.env.BASE_URL}assets/img/icons/remark.png`} alt="" />
                </div>{" "}
                <marquee>{d.remark}</marquee>
              </div>
            ) : null}{" "}
            <CasinoLastResult
              gameType="dum10"
              resultComponent={<Dum10RefResults gameType="dum10" fallbackTitle={DUM10_TITLE} roundId={roundId} />}
            />
          </div>
        ) : null}
      </>
      <ViewMoreModal
        show={showViewMoreModal}
        onHide={() => setShowViewMoreModal(false)}
        title="View More"
        sectionKey={gameName}
      />
    </CasinoTableLayout>
  );
}
